/**
 * Smart Apply — handles "Apply" button routing logic.
 * 
 * When user clicks Apply on a specific product:
 * 1. If not logged in → show Quick OTP modal (one-click signup)
 * 2. If productId provided → skip popup, go directly to flow
 * 3. If no productId (generic) → show popup with pre-selected option
 */
import { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';

const ApplyContext = createContext(null);

const FLOW_ROUTES = {
  admission: '/apply/study-setup',
  visa: '/apply/visa-setup',
  both: '/apply/study-setup',
  rpl: '/rpl',
  skills: '/skill-assessment',
};

export function ApplyProvider({ children }) {
  const [showPopup, setShowPopup] = useState(false);
  const [preSelected, setPreSelected] = useState(null);
  const [pendingProductId, setPendingProductId] = useState(null);

  // OTP modal state
  const [showOTP, setShowOTP] = useState(false);

  const triggerApply = useCallback((productType, productId = null) => {
    setPreSelected(productType);
    setPendingProductId(productId);
    setShowPopup(true);
  }, []);

  const closePopup = useCallback(() => {
    setShowPopup(false);
    setPreSelected(null);
    setPendingProductId(null);
  }, []);

  const triggerOTP = useCallback(() => {
    setShowOTP(true);
  }, []);

  const closeOTP = useCallback(() => {
    setShowOTP(false);
  }, []);

  return (
    <ApplyContext.Provider value={{
      showPopup, preSelected, pendingProductId, triggerApply, closePopup,
      showOTP, triggerOTP, closeOTP,
    }}>
      {children}
    </ApplyContext.Provider>
  );
}

export function useApplyContext() {
  return useContext(ApplyContext);
}

/**
 * Store pre-selected product data so downstream pages can read it.
 * Call this BEFORE smartApply() from product detail pages.
 */
export function storePreselectedProduct(data) {
  localStorage.setItem('unexa_preselected_product', JSON.stringify(data));
}

/** Read and clear stored product */
export function getPreselectedProduct() {
  try {
    const raw = localStorage.getItem('unexa_preselected_product');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearPreselectedProduct() {
  localStorage.removeItem('unexa_preselected_product');
}

/**
 * Hook that returns a smartApply function.
 * Usage: const smartApply = useSmartApply();
 *        smartApply('visa', programId);
 *
 * If not logged in → shows OTP modal, then continues flow after login.
 * If productId provided → skip popup, navigate directly.
 * If no productId → show the "Select Your Journey" popup.
 */
export function useSmartApply() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const applyCtx = useApplyContext();

  return useCallback((productType, productId = null) => {
    const doApply = () => {
      // If a specific product was selected, skip popup entirely
      if (productId) {
        localStorage.setItem('unexa_intent', productType);
        localStorage.setItem('unexa_intent_shown', 'true');
        const route = FLOW_ROUTES[productType];
        if (route) {
          navigate(route);
          return;
        }
      }

      // No specific product — show popup with pre-selected option
      if (applyCtx) {
        applyCtx.triggerApply(productType, productId);
      }
    };

    // Step 1: Must be logged in
    if (!user) {
      // Store the pending route so we can navigate after OTP login
      if (productId) {
        const route = FLOW_ROUTES[productType];
        if (route) {
          localStorage.setItem('unexa_intent', productType);
          localStorage.setItem('unexa_intent_shown', 'true');
          localStorage.setItem('unexa_pending_route', route);
        }
      }
      // Show OTP modal
      if (applyCtx) {
        applyCtx.triggerOTP();
      }
      return;
    }

    doApply();
  }, [user, navigate, applyCtx]);
}
